import { Form, Formik } from 'formik';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import { useEffect, useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { useTranslation } from 'react-i18next';
import { customStyles } from '../../../utils/SelectStyles';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { useNavigate, useParams } from 'react-router-dom';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import * as Yup from 'yup';
import Button from '../../../components/buttons/Button';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import { makesSelectProps } from '../../../utils/selectOptionsProps';

const VehicleModelsEditMainInfo = () => {
  const formRef = useRef(null);
  const { id: makeId, modelId: id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate()

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['models', id],
    invalidateQueryKey: ["models"],
    id,
    singleGetApi: `/admin/makes/${makeId}/models/${id}`,
    editApi: `/admin/models/${id}`,
    itemName: t('model'),
    onComplete: (values: any) => {
      if(values?.vehicle_make_id !== makeId) {
        navigate(`/vehicle-models/${values?.vehicle_make_id}/model-edit/${id}`)
      }
    },
    keysToKeepEmpty: ["brochure_en", "brochure_ar"]
  });

  const { modelOptions: vehicleMakesOptions } = useModelSelectOptions(makesSelectProps());

  const { modelOptions: bodyTypesOptions } = useModelSelectOptions({
    labelKey: 'description.en',
    valueKey: 'id',
    modelRoute: 'admin/body-types',
    queryKey: ['body-types', i18n]
  });

  const initialValues = {
    ...apiData,
    body_types: null,
    vehicle_make: null,
    media: null,

    code: apiData?.code || '',
    slug: apiData?.slug || '',
    order: apiData?.order || '',
    hex_code: apiData?.hex_code || '',

    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    description: {
      en: apiData?.description?.en || '',
      ar: apiData?.description?.ar || ''
    },
    long_description: {
      en: apiData?.long_description?.en || '',
      ar: apiData?.long_description?.ar || ''
    },

    hidden: apiData?.hidden !== undefined ? String(apiData.hidden) : '0',
    featured: apiData?.featured !== undefined ? String(apiData.featured) : '0',

    vehicle_make_id: apiData?.vehicle_make?.id || (vehicleMakesOptions?.length == 1 ? vehicleMakesOptions[0]?.value : ''),
    body_type_ids: apiData?.body_types?.map((item: any) => item.id) || [],

    image: getMedia(apiData?.media, 'image')?.url || '',
    meta_image: getMedia(apiData?.media, 'meta_image')?.url || '',
    brochure_en: getMedia(apiData?.media, 'brochure_en')?.url || '',
    brochure_ar: getMedia(apiData?.media, 'brochure_ar')?.url || ''
  };

  const validationSchema = Yup.object({
    name: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    description: Yup.object().shape({
      ar: Yup.string().required(t('required')),
      en: Yup.string().required(t('required'))
    }),
    vehicle_make_id: Yup.string().required(t('required')),
    code: Yup.string().required(t('required')),
    slug: Yup.string().required(t('required')),
    hidden: Yup.string().oneOf(["0", "1"], t('required')),
    featured: Yup.string().oneOf(["0", "1"], t('required')),
    order: Yup.string().required(t('required')),
    body_type_ids: Yup.array().required(t('required')).min(1, t('required')),
  });

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 1 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 }
          ],
          [
            { columns: 1, items: 1, type: 'switch' },
            { columns: 1, items: 1, type: 'switch' }
          ],
          [
            { columns: 2, items: 1, type: 'textarea' },
            { columns: 2, items: 1 }
          ],
          [{ columns: 2, items: 2, type: 'textarea' }]
        ]}
        buttons={2}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, {setErrors}) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => {
          return (
            <Form>
              <SectionHeader title={t('model_details')}>
                <div className="inputs_grid with_bottom_padding">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding gridSize={6} />
                  <FieldWrapper gridSize={6} />
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('description_en')} inputName={`description[en]`} inputPlaceholder={t('description_en')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('description_ar')} inputName={`description[ar]`} inputPlaceholder={t('description_ar')} input customPadding gridSize={6} />
                  <FieldWrapper
                    title={t('long_description_en')}
                    inputName={`long_description[en]`}
                    inputPlaceholder={t('long_description_en')}
                    textArea
                    customPadding
                    maxLength={50000}
                    gridSize={6}
                  />
                  <FieldWrapper
                    title={t('long_description_ar')}
                    inputName={`long_description[ar]`}
                    inputPlaceholder={t('long_description_ar')}
                    textArea
                    customPadding
                    maxLength={50000}
                    gridSize={6}
                  />
                  <FieldWrapper
                    customPadding
                    title={t('links.body_types')}
                    inputPlaceholder={t('links.body_types')}
                    inputName={`body_type_ids`}
                    options={bodyTypesOptions}
                    selectStyle={customStyles}
                    value={bodyTypesOptions?.filter(item => formik.values?.body_type_ids?.includes(item?.value))}
                    select
                    multi
                    gridSize={6}
                  />
                  <FieldWrapper title={t('slug')} inputName={`slug`} inputPlaceholder={t('slug')} input customPadding gridSize={6} />
                  {vehicleMakesOptions?.length > 1 && <FieldWrapper
                    customPadding
                    title={t('vehicle_make')}
                    inputPlaceholder={t('vehicle_make')}
                    inputName={`vehicle_make_id`}
                    options={vehicleMakesOptions}
                    selectStyle={customStyles}
                    select
                    gridSize={6}
                  />}
                  <FieldWrapper title={t('order_number_in_explore')} inputName={`order`} inputPlaceholder={t('order')} input customPadding gridSize={6} />
                </div>
              </SectionHeader>
              <SectionHeader title={t('advanced_settings')}>
                <div className="switch_container">
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked ? 1 : 0)} active={formik.values.hidden == 1} />
                  <p>
                    {t('hide_from_website')} <span className="side_note">({t('active_means_hide')})</span>
                  </p>
                </div>
                <div className="switch_container" style={{ paddingBottom: '2rem' }}>
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('featured', value?.target?.checked ? 1 : 0)} active={formik.values.featured == 1} />
                  <p>
                    {t('featured')} <span className="side_note">({t('active_means_disabled')})</span>
                  </p>
                </div>
              </SectionHeader>
              <SectionHeader title={t('model_image')}>
                <div className="inputs_grid with_bottom_padding">
                  <FieldWrapper gridSize={6}>
                    <FormUpload errors={formik?.errors.image as string} touched={formik?.touched?.image as boolean} formik={formik} name={`image`} label={t('image')} />
                  </FieldWrapper>
                  <FieldWrapper noPadding gridSize={6} />
                  <FieldWrapper title={t('hex_code')} inputName={`hex_code`} inputPlaceholder={t('hex_code')} input customPadding gridSize={6} />
                </div>
              </SectionHeader>
              <SectionHeader title={t('brochure')} customStyle="last">
                <div className="inputs_grid">
                  <FieldWrapper gridSize={6}>
                    <FormUpload fileName={t('brochure_en')} formik={formik} name={`brochure_en`} label={t('brochure_en')} file />
                  </FieldWrapper>
                  <FieldWrapper gridSize={6}>
                    <FormUpload fileName={t('brochure_ar')} formik={formik} name={`brochure_ar`} label={t('brochure_ar')} file />
                  </FieldWrapper>
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default VehicleModelsEditMainInfo;

//sunt
//Ut qui voluptate facere debitis impedit aut vero.
//Porro enim ea aut ut sunt dolorem. Veritatis voluptatem quas sit odio eos est.
